/**
 * Design tokens for SimulAlert Dashboard v7.
 *
 * The surface tokens (canvas, ink, hairlines, rail, pills) live as CSS custom
 * properties in `src/index.css` so the night theme can be swapped by flipping
 * `data-theme` on <html> without a React re-render. Reference them from JS with
 * `cssVar()` rather than hardcoding a hex, or the night theme will not follow.
 *
 * The indicator and status palettes below are real hex values: they are painted
 * onto canvas (konva) and interpolated for contrast, neither of which can read a
 * CSS variable.
 */

/**
 * Reference a surface token defined in index.css.
 * @param {string} name token name without the `--sa-` prefix, e.g. 'canvas'
 * @return {string} a CSS `var()` expression
 */
export const cssVar = (name) => `var(--sa-${name})`;

/**
 * The day theme's surface values as real hex.
 *
 * Needed because MUI v4 parses `palette` colors — `augmentColor` and
 * `getContrastRatio` do math on them — and throws on a `var()` expression.
 * Anything MUI only writes into CSS (`overrides`, `typography`) should use `T`
 * instead so it follows the night theme.
 *
 * Keep in sync with `:root` in src/index.css.
 */
export const DAY = {
  canvas: '#eeefe9',
  surfaceSoft: '#e5e7e0',
  surfaceCard: '#ffffff',
  elevated: '#ffffff',
  hairline: '#bfc1b7',
  hairlineSoft: '#dcdfd2',
  ink: '#23251d',
  inkActive: '#33342d',
  body: '#4d4f46',
  muted: '#6c6e63',
  mutedSoft: '#9b9c92'
};

/**
 * Surface tokens, as `var()` expressions. Values are in `src/index.css`.
 */
export const T = {
  canvas: cssVar('canvas'),
  surfaceSoft: cssVar('surface-soft'),
  surfaceCard: cssVar('surface-card'),
  elevated: cssVar('elevated'),
  hairline: cssVar('hairline'),
  hairlineSoft: cssVar('hairline-soft'),
  ink: cssVar('ink'),
  inkActive: cssVar('ink-active'),
  body: cssVar('body'),
  muted: cssVar('muted'),
  mutedSoft: cssVar('muted-soft'),
  scrim: cssVar('scrim'),
  rail: cssVar('rail'),
  railHover: cssVar('rail-hover'),
  railActive: cssVar('rail-active'),
  railInk: cssVar('rail-ink'),
  railMuted: cssVar('rail-muted'),
  railHairline: cssVar('rail-hairline'),
  pillActive: cssVar('pill-active'),
  pillTrack: cssVar('pill-track')
};

/**
 * Indicator fills — dots, bars, rails. Bright brand tones, safe at any size
 * because they are never used for text.
 */
export const INDICATOR = {
  accent: '#2c84e0',
  success: '#2c8c66',
  warning: '#f7a501',
  error: '#cd4239',
  violet: '#7c44a6',
  orange: '#f7a501'
};

/**
 * Text tiers — AA-safe deep variants. Use these, not INDICATOR, for type under
 * 24px.
 */
export const INDICATOR_TEXT = {
  accent: '#1078a3',
  success: '#1f6b4c',
  warning: '#8a5e0f',
  error: '#b3352d'
};

/**
 * Standard Response Protocol colors. These are the colors the protocol itself
 * specifies, so they are fixed values rather than design choices.
 */
export const SRP = {
  RED: '#FF0000',
  BLUE: '#0064FF',
  YELLOW: '#FFDC00',
  GREEN: '#00FF50',
  PURPLE: '#B400FF',
  ORANGE: '#FF7800'
};

/**
 * Order statuses are offered in the status picker, most severe first.
 *
 * SECURE, EVACUATE and SHELTER are deliberately absent: they have no AlertType
 * row on the server yet. The picker is built from `AlertTypeObjs`, so they will
 * appear here automatically once the backend accepts them.
 */
export const SET_ORDER = [
  'EMERGENCY',
  'HOLD',
  'MEDICAL',
  'ADMINISTRATOR',
  'ALL_CLEAR',
  'NORMAL'
];

/**
 * What each alert shows on a badge LCD and what it asks staff to do.
 *
 * `group` drives the two sections of the alert picker. SECURE, EVACUATE and
 * SHELTER are listed for completeness but have no AlertType row on the server
 * yet, so the picker — which is built from AlertTypeObjs — simply will not
 * render them until they do.
 */
export const ALERT_DETAILS = {
  HOLD: { group: 'srp', message: 'Hold Alert', action: 'Clear the halls, stay in room' },
  SECURE: { group: 'srp', message: 'Secure Alert', action: 'Get inside, lock outside doors' },
  EVACUATE: { group: 'srp', message: 'Evacuate Alert', action: 'Leave the building to a rally point' },
  SHELTER: { group: 'srp', message: 'Shelter Alert', action: 'Hazard safety strategy in place' },
  MEDICAL: { group: 'other', message: 'Medical Alert', action: 'Nurse badges alerted' },
  ADMINISTRATOR: { group: 'other', message: 'Admin Alert', action: 'Administrator badges alerted' }
};

/**
 * Pick a legible foreground for a solid background.
 *
 * Status colors run from #FFDC00 to #B400FF, so neither white nor near-black
 * works across the whole set. Measures relative luminance and returns the one
 * that will pass contrast.
 *
 * @param {string} hex background color, `#rrggbb`
 * @param {boolean} [soft] return the de-emphasised variant, for secondary text
 * @return {string} a color safe to render on `hex`
 */
export const onColor = (hex, soft) => {
  const n = parseInt(hex.slice(1), 16);
  const luminance =
    (0.2126 * ((n >> 16) & 255) +
      0.7152 * ((n >> 8) & 255) +
      0.0722 * (n & 255)) /
    255;

  return luminance > 0.55
    ? (soft ? 'rgba(26,28,22,0.72)' : '#1a1c16')
    : (soft ? 'rgba(255,255,255,0.82)' : '#ffffff');
};

/** Shared elevation for pills and floating controls. */
export const PILL_SHADOW = '0px 1px 1px rgba(0,0,0,0.04)';

/** Font stacks. */
export const FONT_SANS =
  '"IBM Plex Sans", -apple-system, system-ui, Arial, sans-serif';
export const FONT_MONO =
  '"Source Code Pro", ui-monospace, SFMono-Regular, Menlo, monospace';

/** Width at or above which the persistent left rail replaces the drawer. */
export const RAIL_WIDTH = 1024;

/**
 * Width at or above which a screen shows its detail panel beside the content
 * instead of in place of it. Below this the two do not both fit.
 */
export const SPLIT_WIDTH = 1180;

/** Row heights for tables and list rows. */
export const ROW_HEIGHT = {
  dense: 56,
  comfortable: 72
};
